import React, { useCallback, useEffect, useState } from 'react';

import { Button, Popover } from '@mui/material';

import useIsMobile from 'hooks/use-is-mobile';
import { useConfigs } from 'modules/configs';
import { getCookie, setCookie } from 'utils/cookies';
import isBrowser from 'utils/is-browser';

import useStyles from './styles';

enum ConsentType {
    NECESSARY = 'necessary',
    FUNCTIONAL = 'functional',
    ANALYTICS = 'analytics',
    MARKETING = 'marketing'
}

const CONSENT_TO_USE_COOKIES = 'consent-cookies';

const CookieConsentBanner: React.FC = () => {
    const { classes } = useStyles();
    const { isBot } = useConfigs();

    const isMobile = useIsMobile();

    const [showBanner, setShowBanner] = useState(false);
    const [_consent, setConsent] = useState<Record<ConsentType, boolean>>({
        necessary: true,
        functional: false,
        analytics: false,
        marketing: false
    });

    useEffect(() => {
        if (!isBot) {
            const cookieConsent = getCookie(CONSENT_TO_USE_COOKIES);
            if (!cookieConsent) {
                setShowBanner(true);
            } else {
                const parsed = JSON.parse(cookieConsent);
                setConsent(parsed);
                updateGtmConsent('update', parsed);
            }
        }
    }, [isBot]);

    const updateGtmConsent = (event: 'update' | 'default', consent: Record<ConsentType, boolean>) => {
        if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;

        window.gtag('consent', event, {
            ad_storage: consent.marketing ? 'granted' : 'denied',
            ad_user_data: consent.marketing ? 'granted' : 'denied',
            ad_personalization: consent.marketing ? 'granted' : 'denied',
            analytics_storage: consent.analytics ? 'granted' : 'denied',
            personalization_storage: consent.functional ? 'granted' : 'denied', //так же можно отнести к marketing
            functionality_storage: consent.functional ? 'granted' : 'denied',
            security_storage: consent.necessary ? 'granted' : 'denied'
        });
    };

    const handleAcceptAll = useCallback(() => {
        const consent = { necessary: true, analytics: true, marketing: true, functional: true };

        setConsent(consent);
        setCookie(CONSENT_TO_USE_COOKIES, JSON.stringify(consent), { maxAge: 365 });
        updateGtmConsent('update', consent);

        setShowBanner(false);
    }, []);

    const handleRejectAll = () => {
        const consent = { necessary: true, analytics: false, marketing: false, functional: false };

        setConsent(consent);
        setCookie(CONSENT_TO_USE_COOKIES, JSON.stringify(consent), { maxAge: 365 });
        updateGtmConsent('update', consent);

        setShowBanner(false);
    };

    return (
        <Popover
            open={showBanner}
            anchorEl={isBrowser() ? document.body : null}
            TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}
            keepMounted={false}
            marginThreshold={isMobile ? 0 : undefined}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center'
            }}
            classes={{ root: classes.root, paper: classes.paper }}
        >
            <p className={classes.title}>
                {
                    'We use cookies and similar technologies to operate, maintain, improve our services, and ensure security'
                }
            </p>
            <p className={classes.description}>
                {'See our'}
                &nbsp;
                <a href="https://vulners.com/static/docs/cookie_policy.html" target="_blank" rel="noopener noreferrer">
                    {'Cookie Policy'}
                </a>
                &nbsp;
                {
                    'for details. Click “Accept all” to allow Vulners and its partners to use cookies for these purposes. Select “Reject non-essential” to disable all cookies except those strictly necessary.'
                }
            </p>
            <div className={classes.buttons}>
                <Button
                    onClick={handleRejectAll}
                    fullWidth
                    disableElevation
                    disableRipple
                    variant="outlined"
                    className={classes.reject}
                >
                    {'Reject non-essential'}
                </Button>
                <Button
                    onClick={handleAcceptAll}
                    fullWidth
                    disableElevation
                    disableRipple
                    variant="contained"
                    className={classes.accept}
                >
                    {'Accept all'}
                </Button>
            </div>
        </Popover>
    );
};

export default CookieConsentBanner;
